import { useCallback, useEffect, useRef, useState } from "react";
import { CurrencyInput } from "@/components/shared/currency-input";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { currentFiscalYear, fiscalYearMonths, formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import {
  CopyToFYSelect,
  CREATED_METRIC_PREFIX,
  CREATED_METRICS,
  type GoalRow,
  GoalRowInput,
  SectionHeader,
  savedFieldClass,
  useSaveFlash,
} from "./shared";

// ── Monthly Goal Metric Definitions ──────────────────────────────────

const MONTHLY_GOAL_METRICS: { metricBase: string; label: string; type: "number" | "currency" }[] = [
  { metricBase: "total_units", label: "Monthly Units Sold Goal", type: "number" },
  { metricBase: "gross_sales", label: "Monthly Revenue Goal", type: "currency" },
];

function monthlyMetricKey(metricBase: string, monthIndex: number) {
  return `${CREATED_METRIC_PREFIX}${metricBase}_m${String(monthIndex + 1).padStart(2, "0")}`;
}

export function CreatedGoalsSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedRef = useRef<Record<string, string>>({});

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/targets?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        const rows: GoalRow[] = data.targets
          .filter((t: { metric: string }) => t.metric.startsWith(CREATED_METRIC_PREFIX))
          .map((t: { metric: string; targetValue: number }) => ({
            metric: t.metric,
            value: String(t.targetValue),
          }));
        setGoals(rows);
        const snapshot: Record<string, string> = {};
        for (const r of rows) snapshot[r.metric] = r.value;
        lastSavedRef.current = snapshot;
        onDataChange(rows.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Always show all 4 created metrics in order, pre-populated or empty
  const allGoals: GoalRow[] = CREATED_METRICS.map((m) => {
    const existing = goals.find((g) => g.metric === m.key);
    return existing ?? { metric: m.key, value: "" };
  });

  function updateGoalValue(metric: string, value: string) {
    setGoals((prev) => {
      const exists = prev.find((g) => g.metric === metric);
      if (exists) {
        return prev.map((g) => (g.metric === metric ? { ...g, value } : g));
      }
      return [...prev, { metric, value }];
    });
  }

  const saveMetric = useCallback(
    async (metric: string, value: string) => {
      if (value === (lastSavedRef.current[metric] ?? "")) return;

      const res = await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear,
          targets: [{ metric, targetValue: value === "" ? null : Number(value) }],
        }),
      });

      if (res.ok) {
        lastSavedRef.current[metric] = value;
        flashSaved(metric);
        // Update goals state to include this metric
        setGoals((prev) => {
          const exists = prev.find((g) => g.metric === metric);
          if (value === "") {
            const updated = prev.filter((g) => g.metric !== metric);
            onDataChange(updated.length > 0);
            return updated;
          }
          if (exists) {
            onDataChange(true);
            return prev;
          }
          onDataChange(true);
          return [...prev, { metric, value }];
        });
      }
    },
    [ownerId, fiscalYear, flashSaved, onDataChange],
  );

  function handleBlur(metric: string) {
    const goal = allGoals.find((g) => g.metric === metric);
    if (goal) saveMetric(metric, goal.value);
  }

  async function removeAll() {
    const toDelete = goals.map((g) => ({ metric: g.metric, targetValue: null }));
    if (toDelete.length > 0) {
      await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear, targets: toDelete }),
      });
    }
    setGoals([]);
    lastSavedRef.current = {};
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    if (goals.length === 0) return;
    await fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: targetFY,
        targets: goals
          .filter((g) => g.value !== "")
          .map((g) => ({ metric: g.metric, targetValue: Number(g.value) })),
      }),
    });
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Created Goals" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && goals.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          {!loading && goals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No created goals configured for {formatFY(fiscalYear)}.
              {!readOnly && " Set targets below."}
            </p>
          ) : null}
          <div className="space-y-3">
            {allGoals.map((goal) => (
              <GoalRowInput
                key={goal.metric}
                goal={goal}
                onChangeValue={(v) => updateGoalValue(goal.metric, v)}
                onBlur={() => handleBlur(goal.metric)}
                savedField={savedField}
                disabled={readOnly}
              />
            ))}
          </div>

          {/* Monthly goal breakdowns — used to show "winning vs losing" on the dashboard tracker */}
          <div className="mt-6 space-y-6">
            {MONTHLY_GOAL_METRICS.map((m) => {
              const months = fiscalYearMonths(fiscalYear);
              return (
                <div key={m.metricBase}>
                  <Label className="text-sm">{m.label}</Label>
                  <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                    {months.map((month, i) => {
                      const metric = monthlyMetricKey(m.metricBase, i);
                      const goal = goals.find((g) => g.metric === metric);
                      const value = goal?.value ?? "";
                      const inputClass = savedFieldClass(savedField, metric);
                      const monthLabel = new Date(
                        Number(month.slice(0, 4)),
                        Number(month.slice(5, 7)) - 1,
                        1,
                      ).toLocaleDateString("en-US", { month: "short" });
                      return (
                        <div key={metric} className="space-y-1">
                          <Label
                            htmlFor={`target-${metric}`}
                            className="text-xs text-muted-foreground"
                          >
                            {monthLabel}
                          </Label>
                          {m.type === "currency" ? (
                            <CurrencyInput
                              id={`target-${metric}`}
                              value={value}
                              onChange={(v) => updateGoalValue(metric, v)}
                              onBlur={() => saveMetric(metric, value)}
                              placeholder="Goal"
                              className={cn("h-9 text-sm", inputClass)}
                              disabled={readOnly}
                            />
                          ) : (
                            <Input
                              id={`target-${metric}`}
                              type="number"
                              value={value}
                              onChange={(e) => updateGoalValue(metric, e.target.value)}
                              onBlur={() => saveMetric(metric, value)}
                              placeholder="Goal"
                              className={cn("h-9 text-sm", inputClass)}
                              disabled={readOnly}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
